import { ref, computed } from 'vue';
import useMainApiRequest from '@composables/useMainApiRequest';
import { registerCacheInvalidation } from '@composables/useCache';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

export type UserRole = { roleKey: TmRole; label: string; disabled?: boolean };

const allUsersFetched = ref(false);
const cachedUsers = ref<Record<number, TmUser>>({});
const cachedOrganizations = ref<Record<number, TmOrganization>>({});

registerCacheInvalidation('users', invalidateUsers);
registerCacheInvalidation('organizations', invalidateOrganizations);

export default function useUsers(defUserId?: number) {
  const users = computed(() => Object.values(cachedUsers.value));
  const user = computed<TmUser>(() => {
    if (!defUserId) return {};
    return cachedUsers.value[defUserId] ?? {};
  });
  const organizations = computed(() => Object.values(cachedOrganizations.value));
  const { locale, t } = useI18n();
  const { makeRequest } = useMainApiRequest();
  const router = useRouter();
  const store = useStore();
  const loggedUserId: number = store.state.auth.user.id;

  async function fetchUsers(hiddenFields = false) {
    if (_isCacheAvailable()) return;
    invalidateUsers();

    await makeRequest({
      url: 'users',
      method: 'get',
      params: {
        ...(hiddenFields && { hiddenFields: true }),
      },
      message: {
        error: {
          404: { summary: t('users.no users found'), severity: 'info' },
          default: t('users.error while fetching users'),
        },
      },
      onSuccess: (result: { users: TmUser[] }) => {
        result.users.forEach((user) => {
          if (user.id) cachedUsers.value[user.id] = user;
        });
        allUsersFetched.value = true;
      },
    });
  }

  async function fetchUser(userId = defUserId) {
    if (!userId) throw new Error('No user id provided for the fetch');
    if (_isCacheAvailable({ modelId: userId })) return;
    invalidateUsers(userId);

    await makeRequest({
      url: `users/${userId}`,
      method: 'get',
      message: {
        error: t('users.error while fetching user'),
      },
      onSuccess: (result: { user: TmUser }) => {
        cachedUsers.value[userId] = result.user;
      },
    });
  }

  async function createUser({ email, username, roles, organization, is2svActive }: TmUser) {
    await makeRequest({
      url: `users`,
      method: 'post',
      data: { email, username, roles, organization, is2svActive },
      message: {
        success: t('users.user created'),
        error: t('users.error while creating user'),
      },
      onSuccess: async (result: { user: TmUser }) => {
        const { id, organization, email } = result.user;

        if (id) cachedUsers.value[id] = result.user;
        if (organization && _isNewOrganization(organization)) invalidateOrganizations();
        // send password reset request with the 'create' type
        if (email) requestPasswordReset({ type: 'create', email });
      },
    });
  }

  async function updateUser({
    id,
    email,
    username,
    roles,
    organization,
    is2svActive,
    newPassword,
    oldPassword,
  }: TmUser) {
    if (!id) throw new Error('No user id provided for the update');
    await makeRequest({
      url: `users/${id}`,
      method: 'patch',
      data: { email, username, roles, organization, is2svActive, newPassword, oldPassword },
      message: {
        success: t('users.user saved'),
        error: t('users.error while saving user'),
      },
      onSuccess: async (result: { user: TmUser }) => {
        const { id, organization } = result.user;
        if (id) cachedUsers.value[id] = result.user;
        if (id && loggedUserId == id) {
          // if updating currently logged user -> also update store auth module
          store.commit('auth/UPDATE_AUTH', result.user);
        }
        if (organization && _isNewOrganization(organization)) invalidateOrganizations();
      },
    });
  }

  async function deleteUser(userId = defUserId) {
    if (!userId) throw new Error('No user id provided for the deletion');
    await makeRequest({
      url: `users/${userId}`,
      method: 'delete',
      message: {
        success: t('users.user deleted'),
        error: t('users.failed to delete user'),
      },
      onSuccess: () => {
        delete cachedUsers.value[userId];
      },
    });
  }

  async function requestPasswordReset({
    email,
    type = 'reset',
    leaveAfter = false,
  }: {
    email?: string;
    type?: 'create' | 'reset';
    leaveAfter?: boolean;
  }) {
    if (!email) throw new Error('No e-mail provided for password reset.');
    const host = `${window.location.origin}${import.meta.env.BASE_URL}`;

    await makeRequest({
      url: 'users/password-reset',
      method: 'post',
      timeout: 1000 * 30, // 30 sec (email sending may take some time)
      data: { email, type, lang: locale.value, host },
      message: {
        success: t('users.reset email sent'),
        error: t('users.failed to send reset email'),
      },
      onSuccess: () => {
        // redirect to the login view
        if (leaveAfter) router.push({ name: 'user.login' });
      },
    });
  }

  async function resetPassword({
    userId,
    token,
    email,
    password,
  }: {
    userId: number;
    token: string;
    email: string;
    password: string;
  }) {
    await makeRequest({
      url: `users/${userId}/password-reset/${token}`,
      method: 'post',
      data: { email, password },
      message: {
        success: t('users.password reset successful'),
        error: t('users.failed to reset password'),
      },
      onSuccess: () => {
        // redirect to the login view
        router.push({ name: 'user.login' });
      },
    });
  }

  async function fetchOrganizations() {
    if (_isCacheAvailable({ model: 'organizations' })) return;
    invalidateOrganizations();

    await makeRequest({
      url: 'users/organizations',
      method: 'get',
      message: {
        error: {
          404: { summary: t('users.no organizations found'), severity: 'info' },
          default: t('users.error while fetching organizations'),
        },
      },
      onSuccess: (result: { organizations: TmOrganization[] }) => {
        result.organizations.forEach((org) => {
          if (org.id) cachedOrganizations.value[org.id] = org;
        });
      },
    });
  }

  function _isNewOrganization(orgName: string) {
    return !!orgName && !organizations.value.find((org) => org.name === orgName);
  }

  function _isCacheAvailable({ modelId, model = 'users' }: { modelId?: number; model?: string } = {}) {
    switch (model) {
      case 'users':
        return modelId ? !!cachedUsers.value[modelId] : allUsersFetched.value;
      case 'organizations':
        return !!Object.keys(cachedOrganizations.value).length;
      default:
        return false;
    }
  }

  const allUserRoles: UserRole[] = [
    { roleKey: 'admin', label: 'users.admin' },
    { roleKey: 'userManager', label: 'users.user manager' },
    { roleKey: 'textManager', label: 'users.text manager' },
    { roleKey: 'modelManager', label: 'users.model manager' },
    { roleKey: 'loggedIn', label: 'users.basic user' },
  ];

  const isAdminRoleSelected = (userRoles: UserRole[]) => userRoles.some((role) => role.roleKey === 'admin');

  const shouldBeRestrictedRole = (
    roleKey: UserRole['roleKey'],
    selectedRoles: UserRole[],
    restrictionType: 'disabled' | 'hidden',
  ): boolean => {
    const isAdmin = store.getters['auth/hasRole']('admin');
    const isUserManager = !isAdmin && store.getters['auth/hasRole']('userManager');
    const isAdminRole = roleKey === 'admin';
    const isUserManagerRole = roleKey === 'userManager';
    const isAdminSelected = isAdminRoleSelected(selectedRoles);
    const isEditingSelf = store.state.auth.user.id === user.value.id;
    if (restrictionType === 'hidden')
      return (isUserManager && isAdminRole) || (isUserManager && !isEditingSelf && isUserManagerRole);
    return (isAdmin && isAdminSelected && !isAdminRole) || (isUserManager && isUserManagerRole);
  };

  const getRoleName = (role: TmRole) => {
    const roleLabel = allUserRoles.find((r) => r.roleKey === role)?.label;
    return roleLabel ? t(roleLabel) : role;
  };

  return {
    users,
    user,
    fetchUsers,
    fetchUser,
    createUser,
    updateUser,
    deleteUser,
    requestPasswordReset,
    resetPassword,
    organizations,
    fetchOrganizations,
    invalidateUsers,
    invalidateOrganizations,
    allUserRoles,
    shouldBeRestrictedRole,
    isAdminRoleSelected,
    getRoleName,
    isUserLoggedAs: (role: TmRole) => store.getters['auth/hasRole'](role),
    loggedInUserId: store.state.auth.user.id,
  };
}

export function invalidateUsers(userId?: number) {
  allUsersFetched.value = false;

  userId ? delete cachedUsers.value[userId] : (cachedUsers.value = {});
}

export function invalidateOrganizations() {
  cachedOrganizations.value = {};
}
